/**
 * Map icon registration helpers.
 *
 * Mapbox `symbol` layers reference icons by name (e.g. `icon-image: "site"`).
 * Before a layer can render that icon, the image has to exist on the map
 * instance via `map.addImage(name, …)`. These helpers rasterize a Phosphor
 * React icon to an HTMLImageElement and register it.
 *
 * Why renderToStaticMarkup → SVG data URL → Image:
 *   - Phosphor distributes its glyphs as React SVG components, not raster
 *     assets. We render them to an SVG string at startup so we don't have to
 *     ship a parallel set of PNG sprites or build an SDF generator.
 *   - Mapbox's `addImage` accepts an HTMLImageElement directly, so we go
 *     SVG-string → data URL → Image and hand it over.
 *   - We pass `sdf: false`. Phosphor SVGs rasterize to alpha masks, not
 *     true signed-distance fields, so SDF mode would render incorrectly.
 *     Color is baked in via the `color` prop on the React icon. When we
 *     eventually want runtime tinting (status-driven colors), we'll switch
 *     to a real SDF generator (e.g. tiny-sdf) and flip this flag.
 */

import type { Icon as PhosphorIcon } from "@phosphor-icons/react";
import type { Map as MapboxMap } from "mapbox-gl";
import { createElement } from "react";
import { renderToStaticMarkup } from "react-dom/server";

/**
 * Minimal subset of the Mapbox map surface this module touches. Declared as
 * a structural type so callers can pass either a raw `mapbox-gl` `Map` or a
 * `react-map-gl` `MapRef` (which wraps `Map` but has slightly different
 * optionality under `exactOptionalPropertyTypes: true`) without coercion.
 */
type MapWithImageRegistry = Pick<MapboxMap, "hasImage" | "addImage">;

export interface RegisterPhosphorIconOptions {
  /**
   * Pixel size of the rasterized image. Larger = sharper at high zoom but
   * larger texture footprint. 64 is a good default — Mapbox auto-scales
   * down via `icon-size` and the result is crisp at standard map zooms.
   */
  size?: number;
  /**
   * Color baked into the rasterized SVG. Defaults to the design's ink/graphite
   * (`#2c2a26`).
   */
  color?: string;
  /**
   * Phosphor icon weight. Defaults to `"regular"`. Note: only applies when
   * the React icon component honors the `weight` prop (Phosphor's own do).
   */
  weight?: "thin" | "light" | "regular" | "bold" | "fill" | "duotone";
}

/**
 * Render a Phosphor React icon to an SVG, rasterize, and register it with
 * the given Mapbox map under `name`. No-op if an image of that name is
 * already registered.
 *
 * Idempotent: safe to call across map remounts. Resolves once the image is
 * fully decoded and added to the map.
 */
export async function registerPhosphorIcon(
  map: MapWithImageRegistry,
  name: string,
  Icon: PhosphorIcon,
  options: RegisterPhosphorIconOptions = {}
): Promise<void> {
  if (map.hasImage(name)) return;

  const size = options.size ?? 64;
  const color = options.color ?? "#2c2a26";
  const weight = options.weight ?? "regular";

  // Render the React icon to SVG markup. Phosphor components produce a
  // single <svg> element with `currentColor` strokes; passing `color`
  // resolves currentColor at render-time so the rasterized output has
  // the color baked in.
  // createElement instead of JSX so this stays a `.tsx` import-light module —
  // the Phosphor `Icon` type is a forwardRef component which JSX can render
  // but `createElement` plays more cleanly with the explicit prop typing.
  const svgMarkup = renderToStaticMarkup(createElement(Icon, { size, color, weight }));

  // Data URL avoids any cross-origin, network, or CORS dance. SVG is text
  // so encoding is straightforward.
  const dataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgMarkup)}`;

  await new Promise<void>((resolve, reject) => {
    const img = new Image(size, size);
    img.onload = () => {
      // `addImage` can throw synchronously — most often during a basemap
      // swap or component teardown when the underlying style is mid-flight.
      // Because `onload` runs outside this executor, a bare throw would
      // escape as an uncaught exception (the outer Promise's reject path
      // can't see it). Catch and route through `reject` so callers' `.catch`
      // handlers can decide what to do.
      try {
        // Re-check inside onload to handle the race where two callers
        // register the same icon concurrently — the first to `addImage` wins.
        if (!map.hasImage(name)) {
          map.addImage(name, img, { sdf: false });
        }
        resolve();
      } catch (err) {
        reject(err instanceof Error ? err : new Error(`Failed to addImage "${name}": ${String(err)}`));
      }
    };
    img.onerror = (err) => {
      reject(err instanceof Error ? err : new Error(`Failed to load icon "${name}"`));
    };
    img.src = dataUrl;
  });
}
